import {
  useDeleteZyusetuDefaultValueMutation,
  useUpdateZyusetuDefaultValueMutation,
  ZyusetuFieldKeyFragmentFragment
} from "@/graphql/graphql.ts";
import {useCallback} from "react";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography
} from "@mui/material";
import {useZyusetuFieldValueInput} from "@/components/pages/Zyusetu/useZyusetuFieldValueInput.tsx";
import {FormSchema} from "@/components/pages/Zyusetu/ZyusetuEditorSection.tsx";
import {FormProvider, useForm} from "react-hook-form";
import {zyusetuValueIntoZodValue} from "@/hooks/useGetFirstContent.ts";
import {useConfirmDialog} from "@/hooks/use-confirm-dialog.ts";
import {FileWarning} from "lucide-react";
import {ZyusetuEditType} from "@/types/ZyusetuEditType.ts";
import {ZyusetuAtom} from "@/plugin/jotai-wrapper";
import {useAtom} from "jotai";

interface ZyusetuDefaultValueDialogProps {
  fieldKey: ZyusetuFieldKeyFragmentFragment;
  open: boolean;
  onClose: () => void;
  updateField: (keyId: string, keyName: string, value: FormSchema[string]) => Promise<void>;
  editType?: ZyusetuEditType;
}

export function ZyusetuDefaultValueDialog({
                                            fieldKey,
                                            updateField,
                                            open, onClose,
                                            editType
                                          }: ZyusetuDefaultValueDialogProps) {
  
  const [_, setZyusetu] = useAtom(ZyusetuAtom);
  const updateZyusetuValue = useUpdateZyusetuDefaultValueMutation();
  const deleteZyusetuValue = useDeleteZyusetuDefaultValueMutation()
  
  const deleteDefaultValue = useCallback(async () => {
    if (!fieldKey.defaultValue?.[0]?.id) return;
    
    await deleteZyusetuValue.mutateAsync({
      zyusetuDefaultValueId: fieldKey.defaultValue?.[0]?.id
    })
    
    setZyusetu((prev) => {
      prev.template = {
        ...prev.template,
        sections: prev.template.sections.map(section => ({
          ...section,
          keys: section.keys.map(key => ({
            ...key,
            defaultValue: key.id === fieldKey.id ? [] : key.defaultValue
          }))
        }))
      }
      return prev
    })
    onClose();
  }, [deleteZyusetuValue, fieldKey.defaultValue, fieldKey.id, onClose, setZyusetu])
  
  const updateDefaultValue = useCallback(async (
    keyId: string,
    keyName: string,
    value: FormSchema[string]) => {
    
    const update = await updateZyusetuValue.mutateAsync({
      value: value.value,
      keyId: keyId,
      zyusetuDefaultValueId: fieldKey.defaultValue?.[0]?.id
    })
    
    await updateField(keyId, keyName, value);
    
    setZyusetu((prev) => {
      prev.template = {
        ...prev.template,
        sections: prev.template.sections.map(section => ({
          ...section,
          keys: section.keys.map(key => ({
            ...key,
            defaultValue: key.id === keyId ? [{
              value: value.value,
              id: update.updateZyusetuDefaultValue
            }] : key.defaultValue
          }))
        }))
      }
      return prev
    })
    onClose();
  }, [fieldKey.defaultValue, onClose, setZyusetu, updateField, updateZyusetuValue])
  
  const getFieldInput = useZyusetuFieldValueInput(() => {
  }, updateDefaultValue, editType, true);
  
  const form = useForm<FormSchema>({
    values: {
      [fieldKey.keyName]: zyusetuValueIntoZodValue(fieldKey, fieldKey.defaultValue?.[0]?.value),
    },
    mode: 'onChange'
  })
  const confirmDialog = useConfirmDialog()
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={"md"}
      fullWidth
    >
      <DialogTitle>デフォルト値を設定</DialogTitle>
      <DialogContent>
        <FormProvider {...form}>
          
          <Card
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent sx={{flexGrow: 1}}>
              <Typography mb={3}>
                ここで設定した値は、今後作成されるすべての重説に適応されます。<br/>
                AIが抽出した値がない場合にこの値をデフォルト値として設定します。
              </Typography>
              <Typography sx={{whiteSpace: "pre-wrap"}}>
                {getFieldInput(fieldKey, true)}
              </Typography>
            </CardContent>
            <Divider/>
            <CardActions sx={{display: "flex", justifyContent: "end"}}>
              <Button
                color="primary"
                variant={"text"}
                disableElevation
                onClick={() => {
                  confirmDialog.open({
                    fullWidth: true,
                    icon: <FileWarning color={"error"}/>,
                    title: (
                      <Typography
                        sx={{color: "primary.contrastText"}}
                        fontWeight={"bold"}
                      >
                        削除してもよろしいですか?この動作は取り消せません
                      </Typography>
                    ),
                    children: (
                      <Stack direction={"row"} spacing={2} minWidth={"250px"}>
                        <Button
                          fullWidth
                          variant={'outlined'}
                          onClick={async (_) => confirmDialog.close()}
                        >
                          キャンセル
                        </Button>
                        <Button
                          fullWidth
                          variant={'contained'}
                          onClick={async () => {
                            await deleteDefaultValue();
                            confirmDialog.close();
                          }}
                        >削除する</Button>
                      </Stack>
                    ),
                  });
                }}
                disabled={!fieldKey.defaultValue?.[0]?.id}
              >
                デフォルト値を削除
              </Button>
              <Button
                color="primary"
                variant={"contained"}
                disableElevation
                onClick={() => updateDefaultValue(
                  fieldKey.id,
                  fieldKey.keyName,
                  form.getValues()[fieldKey.keyName]
                )}
              >
                更新
              </Button>
            </CardActions>
          </Card>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
